/**
 * Estilos de diseño: una dirección gráfica entera en un clic.
 *
 * Cada uno junta lo que en el resto del panel se elige por partes —paleta,
 * pareja de letras, cómo pesan los títulos, forma de los botones, de las
 * tarjetas y aire de las secciones— siguiendo cómo se ven hoy las webs de
 * empresa y de producto que la gente reconoce: el blanco con mucho aire de los
 * fabricantes de hardware, el oscuro de precisión de las herramientas de
 * software, la línea de tinta de los cuadernos, el editorial serif, la tienda
 * densa… Los nombres describen el estilo; ninguno dice ni da a entender que
 * una marca lo respalde.
 *
 * Nada de lo que envejece mal en una web profesional: sin resplandores de
 * color ni adornos que compitan con la lectura. Fondos
 * casi blancos o casi negros, un solo color de marca, títulos apretados y
 * secciones con aire, que es por donde van las webs en 2026.
 *
 * Se aplica con las mismas piezas que las partes (`applyColorsToTheme`,
 * `applyFontsToTheme` y los looks de `styleLooks`), así que un estilo es
 * exactamente la suma de sus elecciones y todo se retoca después por separado.
 */
import type { StyleProperties } from '@webbuilder/style-engine'
import type { ProjectTheme, ThemeRole } from './types'
import type { StyleCombinationBaseline } from './types'
import { normalizeProjectTheme } from './normalize'
import { mergeStylePatch } from './stylePatch'
import { alignButtonText, buttonSizes } from './buttonTextSize'
import { applyColorsToTheme, applyFontsToTheme } from './presets'
import { distinctPrimaryColor, paletteColorValues, type ColorPalette } from './palettes'
import {
  BUTTON_LOOK_GROUPS,
  CARD_LOOK_GROUPS,
  SECTION_SPACING_LOOKS,
  applyButtonLookToTheme,
  applyCardLookToTheme,
  applySectionSpacingToTheme,
} from './styleLooks'

/**
 * La paleta de un estilo es una paleta de Color como cualquier otra (ver
 * `ColorPalette` en el tema): los mismos papeles, aplicados por la misma
 * función. Así lo que enseña la tarjeta del estilo y lo que queda al elegir
 * esa paleta en Color es exactamente lo mismo.
 */
export type DesignPalette = Omit<ColorPalette, 'id' | 'tags'>

export type DesignStyle = {
  id: string
  labelKey: string
  descKey: string
  palette: DesignPalette
  /** Filtros de Color cuando se elige sólo la paleta de esta combinación. */
  paletteTags?: string[]
  fonts: { heading: string; body: string; serif?: 'heading' | 'both'; script?: boolean }
  /** Cómo pesan los títulos: grosor y espaciado entre letras. */
  headings: { weight: string; tracking: string }
  /** Una opción por grupo de `BUTTON_LOOK_GROUPS`. */
  button: { shape: string; shadow: string; inner: string; size: string; text: string; weight: string }
  /** Una opción por grupo de `CARD_LOOK_GROUPS`. */
  card: { shape: string; shadow: string; inner: string; spacing: string }
  section: string
  /**
   * La línea bajo la barra de navegación. Por defecto, 1px en el color de
   * borde; los estilos de línea de tinta la llevan en la tinta.
   */
  navLine?: { width: string; color: 'border' | 'ink' }
  /**
   * El botón secundario (letra y contorno) y el enlace en la tinta, no en el
   * color de marca: para los estilos cuyo color de acción no se lee como
   * letra —un lima sobre gris claro—.
   */
  inkButtons?: true
}

type Def = Omit<DesignStyle, 'labelKey' | 'descKey'>
const design = (def: Def): DesignStyle => ({
  ...def,
  palette: { ...def.palette, button: distinctPrimaryColor(def.palette.button, def.palette.accent) },
  labelKey: `design.${def.id}`,
  descKey: `design.${def.id}.desc`,
})

export const DESIGN_STYLES: DesignStyle[] = [
  /* Blanco, gris perla y un azul de acción: el catálogo de hardware. Títulos
     apretados, botones píldora sin sombra y tarjetas sin borde sobre gris. */
  design({
    id: 'product',
    palette: { background: '#FFFFFF', alternate: '#F5F5F7', surface: '#E8E8ED', ink: '#1D1D1F', button: '#0071E3', accent: '#0071E3', card: '#F5F5F7', border: '#D2D2D7' },
    fonts: { heading: 'Inter', body: 'Inter' },
    headings: { weight: '600', tracking: '-0.025em' },
    button: { shape: 'pill', shadow: 'none', inner: 'none', size: 'regular', text: 'normal', weight: 'regular' },
    card: { shape: 'rounded', shadow: 'none', inner: 'none', spacing: 'roomy' },
    section: 'spacious',
  }),
  /* Casi negro con un violeta frío: las herramientas de software de precisión.
     Botones claros pequeños, bordes de pelo y un brillo interior mínimo. */
  design({
    id: 'precision',
    palette: { background: '#08090A', alternate: '#0F1012', surface: '#17181B', ink: '#F7F8F8', button: '#F7F8F8', accent: '#5E6AD2', card: '#0F1012', border: '#23252A' },
    fonts: { heading: 'Inter', body: 'Inter' },
    headings: { weight: '600', tracking: '-0.03em' },
    button: { shape: 'square', shadow: 'none', inner: 'highlight', size: 'compact', text: 'tight', weight: 'medium' },
    card: { shape: 'hairline', shadow: 'none', inner: 'highlight', spacing: 'regular' },
    section: 'balanced',
  }),
  /* Papel blanco y tinta: línea fina del color del texto y sombra plana, como
     los cuadernos de trabajo y sus ilustraciones. */
  design({
    id: 'notebook',
    palette: { background: '#FFFFFF', alternate: '#F7F6F3', surface: '#EFEEEA', ink: '#191919', button: '#191919', accent: '#2383E2', card: '#FFFFFF', border: '#E9E9E7' },
    fonts: { heading: 'Work Sans', body: 'Work Sans' },
    headings: { weight: '700', tracking: '-0.02em' },
    button: { shape: 'inked', shadow: 'hard2d', inner: 'none', size: 'regular', text: 'normal', weight: 'semibold' },
    card: { shape: 'ink', shadow: 'hard2d', inner: 'none', spacing: 'comfortable' },
    section: 'balanced',
    navLine: { width: '1px', color: 'ink' },
  }),
  /* Brutalismo de marca, no de fanzine: hueso, negro y un naranja; bordes de
     2px y sombra dura, pero con la letra y el orden de una web seria. */
  design({
    id: 'brutalist',
    palette: { background: '#F4F4F0', alternate: '#FFFFFF', surface: '#E7E7E0', ink: '#000000', button: '#FF5B37', accent: '#FF5B37', card: '#FFFFFF', border: '#000000' },
    fonts: { heading: 'Space Grotesk', body: 'DM Sans' },
    headings: { weight: '700', tracking: '-0.035em' },
    button: { shape: 'brutal', shadow: 'hard', inner: 'none', size: 'regular', text: 'normal', weight: 'semibold' },
    card: { shape: 'bold', shadow: 'hard', inner: 'none', spacing: 'regular' },
    section: 'compact',
    navLine: { width: '2px', color: 'ink' },
  }),
  /* El estudio de diseño: blanco, negro y un azul vivo, tarjetas grandes y
     redondeadas sobre gris, letra geométrica. */
  design({
    id: 'studio',
    palette: { background: '#FFFFFF', alternate: '#F5F5F5', surface: '#EBEBEB', ink: '#000000', button: '#000000', accent: '#0D99FF', card: '#F5F5F5', border: '#E6E6E6' },
    fonts: { heading: 'Figtree', body: 'Figtree' },
    headings: { weight: '600', tracking: '-0.03em' },
    button: { shape: 'pill', shadow: 'none', inner: 'none', size: 'regular', text: 'normal', weight: 'medium' },
    card: { shape: 'rounded', shadow: 'none', inner: 'none', spacing: 'roomy' },
    section: 'airy',
  }),
  /* Estructura a la vista, como un sistema de componentes: blanco y negro,
     esquinas rectas y bordes negros de 1px. */
  design({
    id: 'wireframe',
    palette: { background: '#FFFFFF', alternate: '#F2F2F2', surface: '#E5E5E5', ink: '#000000', button: '#000000', accent: '#000000', card: '#FFFFFF', border: '#000000' },
    fonts: { heading: 'Roboto', body: 'Roboto' },
    headings: { weight: '700', tracking: '-0.015em' },
    button: { shape: 'sharp', shadow: 'none', inner: 'none', size: 'regular', text: 'normal', weight: 'medium' },
    card: { shape: 'sharp', shadow: 'none', inner: 'none', spacing: 'comfortable' },
    section: 'balanced',
  }),
  /* Plataforma de cursos y creadores: limpia, cálida en el trato, un azul de
     confianza y tarjetas con borde y sombra suave. */
  design({
    id: 'creator',
    palette: { background: '#FFFFFF', alternate: '#F7F7F8', surface: '#EDEDF0', ink: '#111827', button: '#2F6BFF', accent: '#2F6BFF', card: '#FFFFFF', border: '#E5E7EB' },
    fonts: { heading: 'Plus Jakarta Sans', body: 'Plus Jakarta Sans' },
    headings: { weight: '700', tracking: '-0.025em' },
    button: { shape: 'rounded', shadow: 'subtle', inner: 'none', size: 'large', text: 'normal', weight: 'semibold' },
    card: { shape: 'outlined', shadow: 'soft', inner: 'none', spacing: 'comfortable' },
    section: 'balanced',
  }),
  /* La tienda grande: mucha información ordenada, botón amarillo de compra con
     letra oscura, tarjetas con borde fino y poco aire. */
  design({
    id: 'marketplace',
    palette: { background: '#FFFFFF', alternate: '#F3F4F6', surface: '#E6E8EB', ink: '#0F1111', button: '#FFD814', accent: '#007185', card: '#FFFFFF', border: '#D5D9D9' },
    fonts: { heading: 'DM Sans', body: 'DM Sans' },
    headings: { weight: '700', tracking: '-0.01em' },
    button: { shape: 'pill', shadow: 'hairline', inner: 'none', size: 'compact', text: 'normal', weight: 'medium' },
    card: { shape: 'box', shadow: 'none', inner: 'none', spacing: 'compact' },
    section: 'compact',
  }),
  /* La revista: títulos serif grandes y finos, papel cálido y un rojo de
     sección. */
  design({
    id: 'editorial',
    palette: { background: '#FFFFFF', alternate: '#F7F5F0', surface: '#ECE8DF', ink: '#111111', button: '#111111', accent: '#B3261E', card: '#F7F5F0', border: '#E4E0D6' },
    fonts: { heading: 'Instrument Serif', body: 'Source Serif 4', serif: 'both' },
    headings: { weight: '400', tracking: '-0.01em' },
    button: { shape: 'pill', shadow: 'none', inner: 'none', size: 'regular', text: 'normal', weight: 'medium' },
    card: { shape: 'square', shadow: 'none', inner: 'none', spacing: 'comfortable' },
    section: 'airy',
  }),
  /* Lujo discreto: marfil, casi negro y un dorado apagado; serif de alta
     costura, botones rectos en mayúsculas espaciadas y muchísimo aire. */
  design({
    id: 'luxury',
    palette: { background: '#F6F4EF', alternate: '#EFEBE3', surface: '#E4DED2', ink: '#1B1B18', button: '#1B1B18', accent: '#8A7650', card: '#EFEBE3', border: '#D9D3C6' },
    fonts: { heading: 'Cormorant Garamond', body: 'Manrope', serif: 'heading' },
    headings: { weight: '500', tracking: '0em' },
    button: { shape: 'sharp', shadow: 'none', inner: 'none', size: 'wide', text: 'spaced', weight: 'medium' },
    card: { shape: 'square', shadow: 'none', inner: 'none', spacing: 'roomy' },
    section: 'spacious',
  }),
  /* Tipografía internacional: retícula, negro y un rojo, grotesca apretada y
     nada redondeado. */
  design({
    id: 'swiss',
    palette: { background: '#FFFFFF', alternate: '#F2F2F2', surface: '#E5E5E5', ink: '#000000', button: '#E30613', accent: '#E30613', card: '#F2F2F2', border: '#D9D9D9' },
    fonts: { heading: 'Archivo', body: 'Archivo' },
    headings: { weight: '700', tracking: '-0.035em' },
    button: { shape: 'sharp', shadow: 'none', inner: 'none', size: 'regular', text: 'normal', weight: 'semibold' },
    card: { shape: 'square', shadow: 'none', inner: 'none', spacing: 'regular' },
    section: 'balanced',
  }),
  /* Pagos y finanzas modernas: azul noche para los títulos, un violeta de
     acción y tarjetas blancas que flotan un poco sobre gris azulado. */
  design({
    id: 'fintech',
    palette: { background: '#FFFFFF', alternate: '#F6F9FC', surface: '#E3E8EE', ink: '#0A2540', button: '#635BFF', accent: '#0A2540', card: '#FFFFFF', border: '#E3E8EE' },
    fonts: { heading: 'Manrope', body: 'Manrope' },
    headings: { weight: '700', tracking: '-0.025em' },
    button: { shape: 'pill', shadow: 'subtle', inner: 'highlight', size: 'regular', text: 'normal', weight: 'semibold' },
    card: { shape: 'hairline', shadow: 'medium', inner: 'none', spacing: 'comfortable' },
    section: 'airy',
  }),
  /* Consultoría: azul marino profundo, serif sobria en los títulos y bloques
     rectos sobre gris claro. */
  design({
    id: 'consulting',
    palette: { background: '#FFFFFF', alternate: '#F4F6F8', surface: '#E6EAEE', ink: '#051C2C', button: '#051C2C', accent: '#2251FF', card: '#F4F6F8', border: '#D0D7DE' },
    fonts: { heading: 'Source Serif 4', body: 'IBM Plex Sans', serif: 'heading' },
    headings: { weight: '500', tracking: '-0.015em' },
    button: { shape: 'sharp', shadow: 'none', inner: 'none', size: 'regular', text: 'normal', weight: 'semibold' },
    card: { shape: 'square', shadow: 'none', inner: 'none', spacing: 'comfortable' },
    section: 'balanced',
  }),
  /* Galería y arquitectura: piedra cálida, casi negro y un terracota como
     única nota; mucho aire y nada de sombra. */
  design({
    id: 'gallery',
    palette: { background: '#F2F0EB', alternate: '#E9E6DF', surface: '#DDD9D0', ink: '#1C1C1A', button: '#1C1C1A', accent: '#B65C3A', card: '#FAF9F6', border: '#D6D1C6' },
    fonts: { heading: 'Epilogue', body: 'Karla' },
    headings: { weight: '500', tracking: '-0.03em' },
    button: { shape: 'pill', shadow: 'none', inner: 'none', size: 'regular', text: 'normal', weight: 'medium' },
    card: { shape: 'square', shadow: 'none', inner: 'none', spacing: 'roomy' },
    section: 'spacious',
  }),
  /* Clínica y bienestar sin clichés: blanco roto, verde bosque, serif amable
     y tarjetas suaves sin borde. */
  design({
    id: 'calm',
    palette: { background: '#FBFAF7', alternate: '#F1EFE8', surface: '#E5E2D8', ink: '#1F2A24', button: '#2F4A3D', accent: '#7C9A86', card: '#F1EFE8', border: '#E0DDD3' },
    fonts: { heading: 'Fraunces', body: 'Figtree', serif: 'heading' },
    headings: { weight: '400', tracking: '-0.015em' },
    button: { shape: 'pill', shadow: 'none', inner: 'none', size: 'large', text: 'normal', weight: 'medium' },
    card: { shape: 'soft', shadow: 'none', inner: 'none', spacing: 'roomy' },
    section: 'airy',
  }),
  /* Despacho y banca tradicional: tinta azul, dorado viejo, serif clásica y
     cajas con borde fino. */
  design({
    id: 'heritage',
    palette: { background: '#FFFFFF', alternate: '#F5F3EF', surface: '#E8E4DC', ink: '#1B2430', button: '#1B2430', accent: '#8C6D3F', card: '#FFFFFF', border: '#D8D2C6' },
    fonts: { heading: 'Libre Baskerville', body: 'Public Sans', serif: 'heading' },
    headings: { weight: '400', tracking: '0em' },
    button: { shape: 'square', shadow: 'none', inner: 'none', size: 'regular', text: 'normal', weight: 'semibold' },
    card: { shape: 'box', shadow: 'none', inner: 'none', spacing: 'comfortable' },
    section: 'balanced',
  }),
  /* Negro puro de plataforma para desarrolladores: botones blancos, bordes de
     pelo y un aro interior casi invisible. */
  design({
    id: 'developer',
    palette: { background: '#000000', alternate: '#0A0A0A', surface: '#111111', ink: '#EDEDED', button: '#EDEDED', accent: '#0070F3', card: '#0A0A0A', border: '#262626' },
    fonts: { heading: 'Sora', body: 'IBM Plex Sans' },
    headings: { weight: '600', tracking: '-0.04em' },
    button: { shape: 'square', shadow: 'none', inner: 'none', size: 'compact', text: 'tight', weight: 'medium' },
    card: { shape: 'hairline', shadow: 'none', inner: 'ring', spacing: 'regular' },
    section: 'balanced',
  }),
  /* Tecnología cálida: crema, casi negro y un arcilla; serif en los títulos y
     tarjetas blancas con borde. Humano sin dejar de ser serio. */
  design({
    id: 'clay',
    palette: { background: '#FAF9F5', alternate: '#F0EEE6', surface: '#E8E6DC', ink: '#141413', button: '#141413', accent: '#D97757', card: '#FFFFFF', border: '#E3E0D6' },
    fonts: { heading: 'Lora', body: 'DM Sans', serif: 'heading' },
    headings: { weight: '500', tracking: '-0.015em' },
    button: { shape: 'rounded', shadow: 'none', inner: 'none', size: 'regular', text: 'normal', weight: 'medium' },
    card: { shape: 'outlined', shadow: 'hairline', inner: 'none', spacing: 'comfortable' },
    section: 'airy',
  }),
  /* Marca de venta directa: blanco, negro, un tostado; botones rectos anchos en
     mayúsculas y fichas de producto sin borde. */
  design({
    id: 'retail',
    palette: { background: '#FFFFFF', alternate: '#F6F6F4', surface: '#EDEDEA', ink: '#111111', button: '#111111', accent: '#7A5C3E', card: '#F6F6F4', border: '#E2E2DE' },
    fonts: { heading: 'Outfit', body: 'Outfit' },
    headings: { weight: '600', tracking: '-0.02em' },
    button: { shape: 'sharp', shadow: 'none', inner: 'none', size: 'wide', text: 'uppercase', weight: 'semibold' },
    card: { shape: 'square', shadow: 'none', inner: 'none', spacing: 'compact' },
    section: 'balanced',
  }),
  /* Nórdico: gris niebla, pizarra y un azul acero; todo suave, tarjetas
     blancas con borde de pelo y una sombra apenas. */
  design({
    id: 'nordic',
    palette: { background: '#F7F7F5', alternate: '#EEEEEA', surface: '#E2E2DC', ink: '#1E2A2F', button: '#1E2A2F', accent: '#5B7B8A', card: '#FFFFFF', border: '#DEDED8' },
    fonts: { heading: 'Urbanist', body: 'Mulish' },
    headings: { weight: '600', tracking: '-0.02em' },
    button: { shape: 'pill', shadow: 'none', inner: 'none', size: 'regular', text: 'normal', weight: 'medium' },
    card: { shape: 'hairline', shadow: 'subtle', inner: 'none', spacing: 'comfortable' },
    section: 'airy',
  }),
  /* Verde de bosque sobrio, de cafetería de marca: verde casa muy oscuro en la
     tinta, un verde de acción profundo, crema cálido de fondo alterno, un verde
     salvia para las superficies y un dorado viejo como única nota. Serif fina
     en los títulos, botones píldora y tarjetas con borde de pelo. */
  design({
    id: 'evergreen',
    palette: { background: '#FFFFFF', alternate: '#F2F0EB', surface: '#D4E9E2', ink: '#1E3932', button: '#00704A', accent: '#CBA258', card: '#FFFFFF', border: '#E3DED3' },
    fonts: { heading: 'Source Serif 4', body: 'Inter', serif: 'heading' },
    headings: { weight: '400', tracking: '-0.01em' },
    button: { shape: 'pill', shadow: 'none', inner: 'none', size: 'regular', text: 'normal', weight: 'medium' },
    card: { shape: 'hairline', shadow: 'none', inner: 'none', spacing: 'comfortable' },
    section: 'airy',
  }),
  /* Estudio creativo de IA, de noche: grafito casi negro, un lima eléctrico de
     acción y un lima oliva de acento —de la misma familia, más apagado, para
     que no se confunda con el de los botones—. Títulos en negrita
     apretados, botones de esquina corta y tarjetas de borde fino con un brillo
     interior. */
  design({
    id: 'voltage',
    palette: { background: '#0F1113', alternate: '#18191C', surface: '#23262A', ink: '#FFFFFF', button: '#D1FE17', accent: '#A7CD46', card: '#1C1E20', border: '#2A2D32' },
    fonts: { heading: 'Inter', body: 'Inter' },
    headings: { weight: '700', tracking: '-0.03em' },
    button: { shape: 'square', shadow: 'none', inner: 'none', size: 'regular', text: 'normal', weight: 'semibold' },
    card: { shape: 'hairline', shadow: 'none', inner: 'highlight', spacing: 'regular' },
    section: 'balanced',
  }),
  /* App de música de día: gris perla, tinta negra y un lima de acción que sólo
     vive en los botones —el resto lo hacen el negro y el blanco—. Títulos en
     negrita apretados, todo en píldora y tarjetas blancas muy redondas con una
     sombra apenas. */
  design({
    id: 'pulse',
    palette: { background: '#F3F3F1', alternate: '#EAEAE7', surface: '#DFDFDB', ink: '#111111', button: '#D4F26A', accent: '#A7C64C', card: '#FFFFFF', border: '#E4E4E0' },
    fonts: { heading: 'Plus Jakarta Sans', body: 'Inter' },
    headings: { weight: '700', tracking: '-0.03em' },
    button: { shape: 'pill', shadow: 'none', inner: 'none', size: 'large', text: 'normal', weight: 'semibold' },
    card: { shape: 'rounded', shadow: 'subtle', inner: 'none', spacing: 'comfortable' },
    section: 'balanced',
    inkButtons: true,
  }),
  /* Clínica luminosa: blanco, menta muy clara y un azul petróleo profundo en la
     tinta, con un coral de acción y un coral más profundo de acento. Outfit
     en todo, títulos apretados, botones píldora sin sombra y tarjetas muy
     redondas sin borde, con mucho aire. Sale del sitio de Lumen Dental Studio. */
  design({
    id: 'coral',
    palette: { background: '#FFFFFF', alternate: '#F5F8F8', surface: '#EEF3F3', ink: '#0F5257', button: '#F27059', accent: '#E3604E', card: '#F1F5F5', border: '#D1E7E4' },
    fonts: { heading: 'Outfit', body: 'Outfit' },
    headings: { weight: '600', tracking: '-0.025em' },
    button: { shape: 'pill', shadow: 'none', inner: 'none', size: 'regular', text: 'normal', weight: 'regular' },
    card: { shape: 'rounded', shadow: 'none', inner: 'none', spacing: 'roomy' },
    section: 'spacious',
  }),
  /* Direcciones de negocio: identidades completas para empresas y servicios
     profesionales, con superficies sobrias y una jerarquía clara. */
  design({
    id: 'executive',
    palette: { background: '#FFFFFF', alternate: '#F1F4F8', surface: '#E4EAF2', ink: '#18263D', button: '#244B78', accent: '#496B94', card: '#F8FAFC', border: '#D6DFEA' },
    fonts: { heading: 'Public Sans', body: 'Inter' },
    headings: { weight: '600', tracking: '-0.025em' },
    button: { shape: 'square', shadow: 'none', inner: 'none', size: 'regular', text: 'normal', weight: 'semibold' },
    card: { shape: 'hairline', shadow: 'subtle', inner: 'none', spacing: 'comfortable' },
    section: 'balanced',
  }),
  design({
    id: 'advisory',
    palette: { background: '#FAF8F4', alternate: '#F0ECE4', surface: '#E5E0D6', ink: '#282B28', button: '#34493E', accent: '#8B6545', card: '#FFFFFF', border: '#D8D2C7' },
    fonts: { heading: 'IBM Plex Serif', body: 'Public Sans', serif: 'heading' },
    headings: { weight: '500', tracking: '-0.015em' },
    button: { shape: 'rounded', shadow: 'none', inner: 'none', size: 'regular', text: 'normal', weight: 'medium' },
    card: { shape: 'box', shadow: 'none', inner: 'none', spacing: 'roomy' },
    section: 'airy',
  }),
  design({
    id: 'capital',
    palette: { background: '#101C19', alternate: '#172822', surface: '#23372F', ink: '#F5F3EA', button: '#D4B477', accent: '#BFA26B', card: '#1C3028', border: '#365145' },
    fonts: { heading: 'Source Serif 4', body: 'Manrope', serif: 'heading' },
    headings: { weight: '500', tracking: '-0.015em' },
    button: { shape: 'sharp', shadow: 'none', inner: 'none', size: 'wide', text: 'spaced', weight: 'medium' },
    card: { shape: 'sharp', shadow: 'none', inner: 'none', spacing: 'roomy' },
    section: 'airy',
  }),
  design({
    id: 'enterprise',
    palette: { background: '#101827', alternate: '#172236', surface: '#23314A', ink: '#F5F7FC', button: '#AAC4FF', accent: '#94B2EB', card: '#1C2940', border: '#344661' },
    fonts: { heading: 'Manrope', body: 'Inter' },
    headings: { weight: '700', tracking: '-0.03em' },
    button: { shape: 'rounded', shadow: 'none', inner: 'highlight', size: 'regular', text: 'normal', weight: 'semibold' },
    card: { shape: 'hairline', shadow: 'none', inner: 'highlight', spacing: 'comfortable' },
    section: 'balanced',
  }),
  design({
    id: 'counsel',
    palette: { background: '#FEFCFA', alternate: '#F2ECE9', surface: '#E7DCDA', ink: '#302127', button: '#6C263B', accent: '#94425A', card: '#FFFFFF', border: '#DAC8CA' },
    fonts: { heading: 'Libre Baskerville', body: 'Public Sans', serif: 'heading' },
    headings: { weight: '400', tracking: '-0.01em' },
    button: { shape: 'sharp', shadow: 'none', inner: 'none', size: 'regular', text: 'uppercase', weight: 'semibold' },
    card: { shape: 'sharp', shadow: 'none', inner: 'none', spacing: 'comfortable' },
    section: 'airy',
  }),
  design({
    id: 'estate',
    palette: { background: '#F7F5F0', alternate: '#EBE7DE', surface: '#DDD8CC', ink: '#292D31', button: '#343B43', accent: '#846747', card: '#FFFFFF', border: '#D3CCBE' },
    fonts: { heading: 'Cormorant Garamond', body: 'Inter', serif: 'heading' },
    headings: { weight: '600', tracking: '-0.01em' },
    button: { shape: 'sharp', shadow: 'none', inner: 'none', size: 'wide', text: 'spaced', weight: 'medium' },
    card: { shape: 'square', shadow: 'none', inner: 'none', spacing: 'spacious' },
    section: 'spacious',
  }),
  design({
    id: 'industry',
    palette: { background: '#FFFFFF', alternate: '#F0F3F5', surface: '#DEE5EA', ink: '#192733', button: '#1E4F65', accent: '#9B491E', card: '#FFFFFF', border: '#C9D4DC' },
    fonts: { heading: 'Barlow', body: 'IBM Plex Sans' },
    headings: { weight: '600', tracking: '-0.02em' },
    button: { shape: 'square', shadow: 'none', inner: 'none', size: 'regular', text: 'uppercase', weight: 'semibold' },
    card: { shape: 'box', shadow: 'none', inner: 'none', spacing: 'regular' },
    section: 'compact',
  }),
  design({
    id: 'care',
    palette: { background: '#FFFFFF', alternate: '#EEF5F5', surface: '#DDEBEC', ink: '#193C43', button: '#22616E', accent: '#377885', card: '#F5FAFA', border: '#C8DEE0' },
    fonts: { heading: 'DM Sans', body: 'Lato' },
    headings: { weight: '600', tracking: '-0.02em' },
    button: { shape: 'pill', shadow: 'none', inner: 'none', size: 'regular', text: 'normal', weight: 'bold' },
    card: { shape: 'outlined', shadow: 'subtle', inner: 'none', spacing: 'comfortable' },
    section: 'airy',
  }),
  design({
    id: 'impact',
    palette: { background: '#F8F9F4', alternate: '#EBEFE2', surface: '#DDE5CE', ink: '#263826', button: '#3F603B', accent: '#68773C', card: '#FFFFFF', border: '#CCD7C0' },
    fonts: { heading: 'Figtree', body: 'Karla' },
    headings: { weight: '600', tracking: '-0.025em' },
    button: { shape: 'rounded', shadow: 'none', inner: 'none', size: 'regular', text: 'normal', weight: 'medium' },
    card: { shape: 'soft', shadow: 'none', inner: 'none', spacing: 'roomy' },
    section: 'airy',
  }),
  design({
    id: 'venture',
    palette: { background: '#191724', alternate: '#242032', surface: '#322C43', ink: '#F7F4FA', button: '#D6C8F1', accent: '#BBA8DD', card: '#2C263D', border: '#49405D' },
    fonts: { heading: 'Plus Jakarta Sans', body: 'DM Sans' },
    headings: { weight: '600', tracking: '-0.03em' },
    button: { shape: 'square', shadow: 'none', inner: 'highlight', size: 'regular', text: 'normal', weight: 'semibold' },
    card: { shape: 'outlined', shadow: 'none', inner: 'none', spacing: 'comfortable' },
    section: 'balanced',
  }),
  /* Lujo sobrio: paletas contenidas, esquinas rectas y sin sombras. Cada
     variante cambia la voz tipográfica, no sólo el color del acento. */
  design({
    id: 'maison',
    paletteTags: ['elegante', 'calido'],
    palette: { background: '#FAF8F4', alternate: '#F1EDE6', surface: '#E6DFD4', ink: '#24211D', button: '#24211D', accent: '#756754', card: '#F1EDE6', border: '#D8D0C4' },
    fonts: { heading: 'Bodoni Moda', body: 'Tenor Sans', serif: 'heading' },
    headings: { weight: '400', tracking: '-0.01em' },
    button: { shape: 'sharp', shadow: 'none', inner: 'none', size: 'wide', text: 'spaced', weight: 'regular' },
    card: { shape: 'square', shadow: 'none', inner: 'none', spacing: 'roomy' },
    section: 'spacious',
    inkButtons: true,
  }),
  design({
    id: 'runway',
    paletteTags: ['elegante', 'moderno'],
    palette: { background: '#FFFFFF', alternate: '#F5F5F5', surface: '#E8E8E8', ink: '#0D0D0D', button: '#0D0D0D', accent: '#555555', card: '#F5F5F5', border: '#D5D5D5' },
    fonts: { heading: 'Gothic A1', body: 'Gothic A1' },
    headings: { weight: '700', tracking: '-0.04em' },
    button: { shape: 'sharp', shadow: 'none', inner: 'none', size: 'regular', text: 'uppercase', weight: 'medium' },
    card: { shape: 'square', shadow: 'none', inner: 'none', spacing: 'regular' },
    section: 'balanced',
    navLine: { width: '1px', color: 'ink' },
    inkButtons: true,
  }),
  design({
    id: 'couture',
    paletteTags: ['elegante'],
    palette: { background: '#FFFFFF', alternate: '#F7F6F3', surface: '#EAE7E1', ink: '#191816', button: '#191816', accent: '#71685B', card: '#F7F6F3', border: '#DAD6CF' },
    fonts: { heading: 'Italiana', body: 'Hanken Grotesk' },
    headings: { weight: '400', tracking: '0em' },
    button: { shape: 'sharp', shadow: 'none', inner: 'none', size: 'wide', text: 'spaced', weight: 'regular' },
    card: { shape: 'square', shadow: 'none', inner: 'none', spacing: 'roomy' },
    section: 'airy',
    inkButtons: true,
  }),
  design({
    id: 'atelier',
    paletteTags: ['elegante', 'calido'],
    palette: { background: '#F9F6EF', alternate: '#EEE9DD', surface: '#E1D8C7', ink: '#29251C', button: '#29251C', accent: '#405649', card: '#EEE9DD', border: '#D1C7B5' },
    fonts: { heading: 'Marcellus', body: 'Tenor Sans', serif: 'heading' },
    headings: { weight: '400', tracking: '-0.015em' },
    button: { shape: 'sharp', shadow: 'none', inner: 'none', size: 'regular', text: 'uppercase', weight: 'regular' },
    card: { shape: 'sharp', shadow: 'none', inner: 'none', spacing: 'comfortable' },
    section: 'spacious',
    inkButtons: true,
  }),
  design({
    id: 'silk',
    paletteTags: ['elegante', 'suave'],
    palette: { background: '#FCF9F8', alternate: '#F3ECEA', surface: '#E8DAD7', ink: '#322A2A', button: '#322A2A', accent: '#866463', card: '#F3ECEA', border: '#DCCBC7' },
    fonts: { heading: 'DM Serif Display', body: 'Hanken Grotesk', serif: 'heading' },
    headings: { weight: '400', tracking: '-0.015em' },
    button: { shape: 'sharp', shadow: 'none', inner: 'none', size: 'regular', text: 'normal', weight: 'regular' },
    card: { shape: 'square', shadow: 'none', inner: 'none', spacing: 'roomy' },
    section: 'airy',
    inkButtons: true,
  }),
  design({
    id: 'sable',
    paletteTags: ['elegante', 'calido'],
    palette: { background: '#F6F2EB', alternate: '#EDE5D9', surface: '#DFD2C0', ink: '#30271F', button: '#30271F', accent: '#79624C', card: '#EDE5D9', border: '#CFC0AB' },
    fonts: { heading: 'Tenor Sans', body: 'Tenor Sans' },
    headings: { weight: '400', tracking: '-0.035em' },
    button: { shape: 'sharp', shadow: 'none', inner: 'none', size: 'wide', text: 'uppercase', weight: 'regular' },
    card: { shape: 'square', shadow: 'none', inner: 'none', spacing: 'comfortable' },
    section: 'spacious',
    inkButtons: true,
  }),
  design({
    id: 'bordeaux',
    paletteTags: ['elegante', 'calido'],
    palette: { background: '#FCFAF7', alternate: '#F2EDE6', surface: '#E5DCD0', ink: '#281D1D', button: '#542A30', accent: '#795155', card: '#F2EDE6', border: '#D9CCC0' },
    fonts: { heading: 'Prata', body: 'Hanken Grotesk', serif: 'heading' },
    headings: { weight: '400', tracking: '-0.02em' },
    button: { shape: 'sharp', shadow: 'none', inner: 'none', size: 'regular', text: 'spaced', weight: 'regular' },
    card: { shape: 'sharp', shadow: 'none', inner: 'none', spacing: 'roomy' },
    section: 'airy',
    inkButtons: true,
  }),
  design({
    id: 'sculpture',
    paletteTags: ['elegante', 'moderno'],
    palette: { background: '#F4F3F0', alternate: '#EAE8E2', surface: '#DCD8CE', ink: '#292A27', button: '#292A27', accent: '#63675C', card: '#EAE8E2', border: '#CFCBC1' },
    fonts: { heading: 'Questrial', body: 'Hanken Grotesk' },
    headings: { weight: '400', tracking: '-0.02em' },
    button: { shape: 'sharp', shadow: 'none', inner: 'none', size: 'regular', text: 'normal', weight: 'regular' },
    card: { shape: 'square', shadow: 'none', inner: 'none', spacing: 'roomy' },
    section: 'airy',
    inkButtons: true,
  }),
  design({
    id: 'archive',
    paletteTags: ['elegante', 'moderno'],
    palette: { background: '#FBFBFA', alternate: '#F0F0EE', surface: '#E2E2DE', ink: '#171817', button: '#171817', accent: '#565953', card: '#F0F0EE', border: '#D1D2CD' },
    fonts: { heading: 'Hanken Grotesk', body: 'Hanken Grotesk' },
    headings: { weight: '500', tracking: '-0.025em' },
    button: { shape: 'sharp', shadow: 'none', inner: 'none', size: 'regular', text: 'uppercase', weight: 'medium' },
    card: { shape: 'sharp', shadow: 'none', inner: 'none', spacing: 'comfortable' },
    section: 'spacious',
    inkButtons: true,
  }),
  design({
    id: 'noir',
    paletteTags: ['elegante'],
    palette: { background: '#111110', alternate: '#1B1B19', surface: '#292925', ink: '#F5F2EB', button: '#F5F2EB', accent: '#B9AD96', card: '#1B1B19', border: '#45433C' },
    fonts: { heading: 'Libre Caslon Display', body: 'Hanken Grotesk', serif: 'heading' },
    headings: { weight: '400', tracking: '-0.02em' },
    button: { shape: 'sharp', shadow: 'none', inner: 'none', size: 'wide', text: 'spaced', weight: 'regular' },
    card: { shape: 'sharp', shadow: 'none', inner: 'none', spacing: 'roomy' },
    section: 'spacious',
    inkButtons: true,
  }),
  /* Escenario: crema y un violeta casi negro de alterno, así las secciones van
     claro · oscuro · claro. Títulos condensados (Oswald) y botones píldora en
     rosa. Sale del sitio de un músico que hizo la IA y le gustó (2026-10-22). */
  design({
    id: 'encore',
    paletteTags: ['elegante', 'moderno'],
    palette: { background: '#FAF6F0', alternate: '#1A1625', surface: '#EBE7E2', ink: '#27252D', button: '#F472B6', accent: '#C76198', card: '#FDFBF9', border: '#E1DDD9' },
    fonts: { heading: 'Oswald', body: 'Barlow' },
    headings: { weight: '400', tracking: '-0.01em' },
    button: { shape: 'pill', shadow: 'none', inner: 'none', size: 'regular', text: 'normal', weight: 'medium' },
    card: { shape: 'square', shadow: 'none', inner: 'none', spacing: 'comfortable' },
    section: 'airy',
  }),
  /* Marcas cercanas: una voz display distinta por estilo, con textos sans
     legibles, color contenido y formas suaves. Los títulos de un solo peso
     se mantienen en 400 para conservar su dibujo original. */
  design({
    id: 'sorbet',
    paletteTags: ['calido', 'vibrante'],
    palette: { background: '#FFF9F5', alternate: '#FCEDE5', surface: '#F3D7C9', ink: '#382332', button: '#8F3455', accent: '#C96048', card: '#FCEDE5', border: '#E7CFC3' },
    fonts: { heading: 'Shrikhand', body: 'DM Sans', serif: 'heading' },
    headings: { weight: '400', tracking: '-0.015em' },
    button: { shape: 'pill', shadow: 'none', inner: 'none', size: 'regular', text: 'normal', weight: 'semibold' },
    card: { shape: 'rounded', shadow: 'none', inner: 'none', spacing: 'comfortable' },
    section: 'airy',
  }),
  design({
    id: 'wiggle',
    paletteTags: ['moderno', 'suave'],
    palette: { background: '#F7FBF8', alternate: '#E8F1EB', surface: '#D3E4DC', ink: '#163B35', button: '#176D5E', accent: '#CB652A', card: '#E8F1EB', border: '#BED3C9' },
    fonts: { heading: 'Chicle', body: 'Manrope' },
    headings: { weight: '400', tracking: '-0.015em' },
    button: { shape: 'tonalRounded', shadow: 'none', inner: 'none', size: 'regular', text: 'normal', weight: 'semibold' },
    card: { shape: 'soft', shadow: 'hairline', inner: 'none', spacing: 'comfortable' },
    section: 'balanced',
  }),
  design({
    id: 'papaya',
    paletteTags: ['calido', 'vibrante'],
    palette: { background: '#FFF9F0', alternate: '#F8EAD4', surface: '#EDD4B0', ink: '#422A19', button: '#A23E27', accent: '#DC7C35', card: '#F8EAD4', border: '#DCC7A9' },
    fonts: { heading: 'Spicy Rice', body: 'Work Sans', serif: 'heading' },
    headings: { weight: '400', tracking: '0em' },
    button: { shape: 'rounded', shadow: 'none', inner: 'none', size: 'regular', text: 'normal', weight: 'semibold' },
    card: { shape: 'outlined', shadow: 'none', inner: 'none', spacing: 'comfortable' },
    section: 'airy',
  }),
  design({
    id: 'sunbeam',
    paletteTags: ['calido', 'vibrante'],
    palette: { background: '#FFFDF1', alternate: '#F5EFCF', surface: '#E8DFAA', ink: '#3E3519', button: '#8B4A24', accent: '#BA8A20', card: '#F5EFCF', border: '#D9CCA1' },
    fonts: { heading: 'Lilita One', body: 'Nunito' },
    headings: { weight: '400', tracking: '-0.015em' },
    button: { shape: 'tonal', shadow: 'none', inner: 'none', size: 'regular', text: 'normal', weight: 'semibold' },
    card: { shape: 'soft', shadow: 'none', inner: 'none', spacing: 'comfortable' },
    section: 'balanced',
  }),
  design({
    id: 'bounce',
    paletteTags: ['moderno', 'suave'],
    palette: { background: '#FAF9FF', alternate: '#EEEBFA', surface: '#DCD6F1', ink: '#30274F', button: '#5941A1', accent: '#C86C46', card: '#EEEBFA', border: '#CCC3E4' },
    fonts: { heading: 'Paytone One', body: 'Public Sans' },
    headings: { weight: '400', tracking: '-0.015em' },
    button: { shape: 'rounded', shadow: 'none', inner: 'none', size: 'regular', text: 'normal', weight: 'semibold' },
    card: { shape: 'rounded', shadow: 'hairline', inner: 'none', spacing: 'comfortable' },
    section: 'airy',
  }),
  design({
    id: 'clubhouse',
    paletteTags: ['moderno', 'suave'],
    palette: { background: '#FAFBF2', alternate: '#EAF0D7', surface: '#D8E3BB', ink: '#25351F', button: '#365C35', accent: '#8C9E3B', card: '#EAF0D7', border: '#C5D1A9' },
    fonts: { heading: 'Dela Gothic One', body: 'IBM Plex Sans' },
    headings: { weight: '400', tracking: '-0.015em' },
    button: { shape: 'pill', shadow: 'none', inner: 'none', size: 'regular', text: 'normal', weight: 'semibold' },
    card: { shape: 'outlined', shadow: 'none', inner: 'none', spacing: 'comfortable' },
    section: 'spacious',
  }),
  design({
    id: 'bagel',
    paletteTags: ['calido', 'vibrante'],
    palette: { background: '#FCF8F1', alternate: '#F1E7DA', surface: '#E4D2BB', ink: '#3D2C24', button: '#86412B', accent: '#B47A42', card: '#F1E7DA', border: '#D6C2AA' },
    fonts: { heading: 'Bagel Fat One', body: 'Figtree' },
    headings: { weight: '400', tracking: '-0.015em' },
    button: { shape: 'rounded', shadow: 'none', inner: 'none', size: 'regular', text: 'normal', weight: 'semibold' },
    card: { shape: 'soft', shadow: 'hairline', inner: 'none', spacing: 'comfortable' },
    section: 'airy',
  }),
  design({
    id: 'confetti',
    paletteTags: ['moderno', 'suave'],
    palette: { background: '#FCFAFF', alternate: '#EFEAF7', surface: '#DFD6EE', ink: '#332745', button: '#65508C', accent: '#BC596F', card: '#EFEAF7', border: '#CFC2E1' },
    fonts: { heading: 'Coiny', body: 'Mulish' },
    headings: { weight: '400', tracking: '0em' },
    button: { shape: 'pill', shadow: 'none', inner: 'none', size: 'regular', text: 'normal', weight: 'semibold' },
    card: { shape: 'rounded', shadow: 'subtle', inner: 'none', spacing: 'comfortable' },
    section: 'balanced',
  }),
  design({
    id: 'block-party',
    paletteTags: ['calido', 'vibrante'],
    palette: { background: '#FFF9F4', alternate: '#F6E9DD', surface: '#EBD5C0', ink: '#233346', button: '#2C526D', accent: '#CB7235', card: '#F6E9DD', border: '#D9C5B5' },
    fonts: { heading: 'Bowlby One SC', body: 'Barlow' },
    headings: { weight: '400', tracking: '0em' },
    button: { shape: 'inked', shadow: 'hard2d', inner: 'none', size: 'regular', text: 'normal', weight: 'semibold' },
    card: { shape: 'ink', shadow: 'hard2d', inner: 'none', spacing: 'comfortable' },
    section: 'balanced',
  }),

]

/** El `presetId` que deja un estilo aplicado: cualquier retoque lo pasa a `custom`. */
export const designPresetId = (item: DesignStyle) => `design:${item.id}`

export function activeDesignStyleId(theme: ProjectTheme): string | null {
  return DESIGN_STYLES.find((item) => theme.presetId === designPresetId(item))?.id ?? null
}

/** Captura sólo lo que una combinación gobierna; nunca el logo del cliente. */
function combinationBaseline(theme: ProjectTheme, id: string): StyleCombinationBaseline {
  return {
    id,
    colors: structuredClone(theme.colors),
    roles: structuredClone(theme.roles),
    ...(theme.roleBreakpoints ? { roleBreakpoints: structuredClone(theme.roleBreakpoints) } : {}),
    colorComponents: structuredClone(theme.colorComponents),
    typographyComponents: structuredClone(theme.typographyComponents),
    buttonComponents: structuredClone(theme.buttonComponents),
    cardComponents: structuredClone(theme.cardComponents),
    sectionComponents: structuredClone(theme.sectionComponents),
    iconComponents: structuredClone(theme.iconComponents),
  }
}

/** Migra proyectos que ya tenían una combinación exacta antes de existir Restart. */
export function ensureStyleCombinationBaseline(theme: ProjectTheme): ProjectTheme {
  if (theme.styleCombinationBaseline) return theme
  const item = DESIGN_STYLES.find((candidate) => theme.presetId === designPresetId(candidate))
  return item ? { ...theme, styleCombinationBaseline: combinationBaseline(theme, item.id) } : theme
}

function paletteValues(palette: DesignPalette) {
  return paletteColorValues({ ...palette, id: '', tags: [] })
}

function fontCss(name: string, serif: boolean) {
  return `'${name}', ${serif ? 'serif' : 'sans-serif'}`
}

const HEADING_ROLES: ThemeRole[] = ['heading1', 'heading2', 'heading3', 'heading4', 'heading5', 'heading6']

/**
 * El peso y el espaciado de los títulos, en sus roles y en sus estilos de
 * texto: un serif fino no quiere el mismo trazo que una grotesca apretada, y
 * con la fuente sola todos salían con el 300 de fábrica.
 */
function withHeadings(theme: ProjectTheme, item: DesignStyle): ProjectTheme {
  const patch: Partial<StyleProperties> = { fontWeight: item.headings.weight, letterSpacing: item.headings.tracking }
  const roles = { ...theme.roles }
  for (const role of HEADING_ROLES) roles[role] = mergeStylePatch(roles[role], patch)
  return {
    ...theme,
    roles,
    typographyComponents: theme.typographyComponents.map((component) => (
      component.role && HEADING_ROLES.includes(component.role) ? { ...component, styles: mergeStylePatch(component.styles, patch) } : component
    )),
  }
}

/**
 * La barra y el footer como en las webs de producto de hoy: la barra con el
 * mismo fondo que la página y una línea fina debajo —la separación la da la
 * línea y el desenfoque al bajar, no una franja de otro color—; el footer sobre
 * la tinta. El relleno de cada uno no se toca.
 */
function withStructuralSections(theme: ProjectTheme, item: DesignStyle): ProjectTheme {
  const line = item.navLine ?? { width: '1px', color: 'border' as const }
  const lineColor = line.color === 'ink' ? 'var(--wb-text)' : 'var(--wb-border)'
  const patches: Record<string, Partial<StyleProperties>> = {
    'section-nav': {
      background: 'var(--wb-background)', backgroundColor: 'var(--wb-background)', backgroundImage: '',
      border: '', borderTop: '', borderRight: '', borderLeft: '', outline: '', outlineOffset: '',
      borderWidth: '0px', borderStyle: 'solid', borderColor: lineColor, borderBottom: `${line.width} solid ${lineColor}`,
    },
    'section-footer': {
      background: 'var(--wb-text)', backgroundColor: 'var(--wb-text)', backgroundImage: '',
    },
  }
  return {
    ...theme,
    sectionComponents: theme.sectionComponents.map((component) => (
      patches[component.id] ? { ...component, styles: mergeStylePatch(component.styles, patches[component.id]!) } : component
    )),
  }
}

/** El secundario y el enlace en la tinta. Ver `DesignStyle.inkButtons`. */
function withInkButtons(theme: ProjectTheme, item: DesignStyle): ProjectTheme {
  if (!item.inkButtons) return theme
  /* La tinta, que en una sección oscura pasa a blanco sola. Y no
     `var(--wb-text)` a secas: `normalize` lo lee como un secundario viejo y
     lo devuelve al color de marca. */
  const ink = 'var(--wb-auto-text-color, var(--wb-text))'
  const patches: Record<string, Partial<StyleProperties>> = {
    'button-secondary': { color: ink, borderColor: ink },
    'button-link': { color: ink },
  }
  const roles = { ...theme.roles }
  const buttonComponents = theme.buttonComponents.map((component) => {
    const patch = patches[component.id]
    if (!patch) return component
    if (component.role) roles[component.role] = mergeStylePatch(roles[component.role], patch)
    return { ...component, styles: mergeStylePatch(component.styles, patch) }
  })
  return { ...theme, roles, buttonComponents }
}

export function applyDesignStyleToTheme(theme: ProjectTheme, item: DesignStyle): ProjectTheme {
  const step = (next: Parameters<typeof normalizeProjectTheme>[0], previous: ProjectTheme) => normalizeProjectTheme(next, previous)
  let next = step(applyColorsToTheme(theme, paletteValues(item.palette)), theme)
  next = step(applyFontsToTheme(
    next,
    fontCss(item.fonts.heading, Boolean(item.fonts.serif)),
    fontCss(item.fonts.body, item.fonts.serif === 'both'),
  ), next)
  next = step(withHeadings(next, item), next)
  for (const group of BUTTON_LOOK_GROUPS) {
    const look = group.options.find((option) => option.id === item.button[group.id as keyof DesignStyle['button']])
    if (look) next = step(applyButtonLookToTheme(next, look), next)
  }
  for (const group of CARD_LOOK_GROUPS) {
    const look = group.options.find((option) => option.id === item.card[group.id as keyof DesignStyle['card']])
    if (look) next = step(applyCardLookToTheme(next, look), next)
  }
  const spacing = SECTION_SPACING_LOOKS.find((option) => option.id === item.section)
  if (spacing) next = step(applySectionSpacingToTheme(next, spacing), next)
  next = step(withStructuralSections(next, item), next)
  next = step(withInkButtons(next, item), next)
  /* Los botones, un paso por debajo del párrafo: ningún estilo decide su letra. */
  next = step(alignButtonText(next, buttonSizes(next)), next)
  const selected = { ...next, presetId: designPresetId(item) }
  return { ...selected, styleCombinationBaseline: combinationBaseline(selected, item.id) }
}
